import React, { useContext, useEffect, useState } from 'react';
import { withFirebase, FirebaseContext } from '../../firebase';
import { AuthUserContext } from '../../session';
import { CChartLine } from '@coreui/react-chartjs';
import { registerables, Chart } from 'chart.js';
import { fetchObservationsFromFHIR } from '../RhythmCard/FhirHelper';
import Logger from 'src/utils/Logger';

Chart.register(...registerables);

const GoogleFitCaloriesChart = () => {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);
  const [caloriesData, setCaloriesData] = useState([]);
  const [fhirCaloriesData, setFhirCaloriesData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllCaloriesData = async () => {
      try {
        // Get first and last day of current month
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        // Fetch Google Fit data (placeholder function)
        const fetchGoogleFitData = async () => {
            // This would integrate with Google Fit API
            // For now, return empty array as placeholder
            return [];
        };
        const googleFitData = await fetchGoogleFitData();
        
        // Fetch FHIR data
        const fhirData = await fetchObservationsFromFHIR(firstDay, lastDay, authUser.authUser);
        const caloriesObservations = fhirData.find(obs => obs.type === 'Calories')?.data || [];

        // Process and combine the data
        const combinedLabels = [];
        const googleFitValues = [];
        const fhirValues = [];

        // Create arrays for the full month
        const daysInMonth = lastDay.getDate();
        for (let i = 0; i < daysInMonth; i++) {
          const date = new Date(firstDay);
          date.setDate(i + 1);
          // Format date function
          const formatDate = (date) => {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          };
          combinedLabels.push(formatDate(date));
          
          // Get Google Fit value for this day (or 0)
          googleFitValues[i] = caloriesData[i] || 0;
          
          // Get FHIR value for this day (or 0)
          fhirValues[i] = caloriesObservations[i] || 0;
        }

        setLabels(combinedLabels);
        setCaloriesData(googleFitValues);
        setFhirCaloriesData(fhirValues);

      } catch (err) {
        setError('Error fetching calories data');
        Logger.error('Error in fetchAllCaloriesData:', err);
      }
    };

    if (authUser?.authUser) {
      fetchAllCaloriesData();
    }
  }, [authUser]);

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'Google Fit Calories',
        data: caloriesData,
        fill: false,
        backgroundColor: 'rgba(255,99,132,0.2)',
        borderColor: 'rgba(255,99,132,1)',
      },
      {
        label: 'FHIR Calories',
        data: fhirCaloriesData,
        fill: false,
        backgroundColor: 'rgba(54,162,235,0.2)',
        borderColor: 'rgba(54,162,235,1)',
      }
    ],
  };

  const options = {
    scales: {
      x: {
        type: 'category',
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Calories'
        }
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y || 0;
            return `${datasetLabel}: ${value.toFixed(0)} calories`;
          }
        }
      },
      legend: {
        display: true,
        position: 'top'
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  return (
    <div>
      <h2>Calories Last Month</h2>
      {error && <p>Error: {error}</p>}
      <CChartLine data={chartData} options={options} />
    </div>
  );
};

export default withFirebase(GoogleFitCaloriesChart);
